import { apiBaseUrl } from '../env';
import { locale, t } from '../i18n';

import type {
  ApiErrorDetail,
  ApiErrorOptions,
  ApiResponse,
  ErrorEnvelope,
  PhotoLocation,
  QueryValue,
  RequestOptions,
} from './http-types';

export type {
  ApiErrorDetail,
  ApiResponse,
  HttpMethod,
  QueryValue,
  RequestOptions,
} from './http-types';

const HTTP_STATUS_UNAUTHORIZED = 401;
const HTTP_STATUS_FORBIDDEN = 403;
const HTTP_STATUS_NOT_FOUND = 404;
const HTTP_STATUS_NO_CONTENT = 204;
const NETWORK_FAILURE_STATUS = 0;
const NETWORK_ERROR_CODE = 'NETWORK_ERROR';
const NETWORK_ERROR_MESSAGE = t(
  '无法连接服务器，请确认后端已启动、代理配置正确',
);
let requestTenant: string | null = null;
export const setRequestTenant = (organizationId: string | null): boolean => {
  const changed = requestTenant !== null && requestTenant !== organizationId;
  requestTenant = organizationId;
  return changed;
};

/**
 * A failed API call, in the one shape the UI handles.
 *
 * `status === 0` is the "never reached the API" case (offline, DNS, proxy down),
 * which is deliberately the same class so callers do not need a second branch — see
 * `networkRequestFailed`.
 */
export class ApiError extends Error {
  /** HTTP 状态码；0 表示请求未获得服务端响应。 */
  public readonly status: number;
  /** 稳定业务错误码。 */
  public readonly code: string;
  /** 已校验的字段详情，在构造边界补齐。 */
  public readonly details: readonly ApiErrorDetail[];

  /** 保存已校验的 API 错误，不把原始响应或请求凭据带入 UI。 */
  constructor(args: ApiErrorOptions) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.details = args.details ?? [];
  }

  /** No valid session: send the user to the login page. */
  public get requiresAuthentication(): boolean {
    return this.status === HTTP_STATUS_UNAUTHORIZED;
  }

  /** Signed in but not allowed: show a 403 view, never a login form. */
  public get accessWasDenied(): boolean {
    return this.status === HTTP_STATUS_FORBIDDEN;
  }

  /** 资源不存在，不等同于会话失效或请求取消。 */
  public get resourceWasNotFound(): boolean {
    return this.status === HTTP_STATUS_NOT_FOUND;
  }

  /** The request never reached the API. */
  public get networkRequestFailed(): boolean {
    return this.status === NETWORK_FAILURE_STATUS;
  }
}

/** 收窄统一 API 错误，供表单和错误视图安全读取状态及字段信息。 */
export const isApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError;

/** Joins the configured origin with a path and appends the defined query values. */
export const buildUrl = (
  path: string,
  search?: Readonly<Record<string, QueryValue>>,
): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(search ?? {})) {
    if (value === undefined || value === null) continue;
    query.set(key, String(value));
  }
  const url = `${apiBaseUrl}${path}`;
  if (query.size === 0) return url;
  return `${url}?${query.toString()}`;
};

/** 容忍代理返回 HTML 或空响应，但不伪造错误信封。 */
const parseJson = (text: string): unknown => {
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A proxy or a crash can answer with HTML. The status still carries the meaning;
    // the body is only used for the message and is not worth failing over.
    return undefined;
  }
};

const isApiResponse = (payload: unknown): payload is ApiResponse<unknown> =>
  typeof payload === 'object' &&
  payload !== null &&
  'code' in payload &&
  typeof payload.code === 'string' &&
  'success' in payload &&
  typeof payload.success === 'boolean' &&
  'message' in payload &&
  typeof payload.message === 'string' &&
  'data' in payload;

/** 在外部响应边界逐项校验字段详情，拒绝 null 和非字符串字段。 */
const detailHasValidFields = (detail: unknown): detail is ApiErrorDetail =>
  typeof detail === 'object' &&
  detail !== null &&
  'path' in detail &&
  typeof detail.path === 'string' &&
  'message' in detail &&
  typeof detail.message === 'string';

/** 保留 HTTP 失败语义，只从已校验的信封字段构造 UI 错误。 */
const toApiError = (response: Response, payload: unknown): ApiError => {
  const envelope = (
    typeof payload === 'object' && payload !== null ? payload : {}
  ) as ErrorEnvelope;
  const details = Array.isArray(envelope.details)
    ? envelope.details.filter(detailHasValidFields)
    : [];
  const code =
    typeof envelope.code === 'string' && envelope.code !== ''
      ? envelope.code
      : `HTTP_${response.status}`;
  const message =
    typeof envelope.message === 'string' && envelope.message !== ''
      ? envelope.message
      : t('请求失败（HTTP {{status}}）', { status: response.status });
  return new ApiError({
    status: response.status,
    code,
    message,
    details,
  });
};

/**
 * Whether a thrown value is a caller-initiated abort rather than a failure.
 *
 * react-query and the route loaders cancel with an `AbortSignal`, and an abort is a normal end to
 * a request — not something to render an error for.
 */
export const requestWasAborted = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

/**
 * Calls the API and answers with the envelope's data.
 *
 * `credentials: 'include'` is not optional: the session lives in an HttpOnly cookie the
 * API sets, and the browser only sends it back when the request is allowed to carry
 * credentials.
 */
export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', search, body, form, signal } = options;
  const headers = new Headers({
    accept: 'application/json',
    'accept-language': locale,
  });
  if (requestTenant) headers.set('x-organization-id', requestTenant);

  let payload: BodyInit | undefined;
  if (form !== undefined) {
    // The browser must set Content-Type itself, so that it can add the boundary.
    payload = form;
  } else if (body !== undefined) {
    headers.set('content-type', 'application/json');
    payload = JSON.stringify(body);
  }

  let defaultTimeoutMs = method === 'GET' ? 30_000 : 60_000;
  if (form !== undefined) defaultTimeoutMs = 300_000;
  const timeoutMs = options.timeoutMs ?? defaultTimeoutMs;
  if (
    !Number.isFinite(timeoutMs) ||
    timeoutMs <= 0 ||
    timeoutMs > 2_147_483_647
  )
    throw new RangeError('Invalid request timeout');
  const deadline = new AbortController();
  const requestSignal = signal
    ? AbortSignal.any([signal, deadline.signal])
    : deadline.signal;
  const timer = setTimeout(
    () => deadline.abort(new DOMException('Request timed out', 'TimeoutError')),
    timeoutMs,
  );
  try {
    const url = buildUrl(path, search);
    const response = await fetch(url, {
      method,
      headers,
      body: payload,
      signal: requestSignal,
      credentials: 'include',
    });
    if (response.status === HTTP_STATUS_NO_CONTENT) return undefined as T;
    const decoded = parseJson(await response.text());
    if (!response.ok) throw toApiError(response, decoded);
    if (!isApiResponse(decoded)) {
      throw new ApiError({
        status: response.status,
        code: 'INVALID_RESPONSE',
        message: t('服务器返回格式异常'),
      });
    }
    if (!decoded.success) throw toApiError(response, decoded);
    return decoded.data as T;
  } catch (error) {
    if (signal?.aborted && requestSignal.reason === signal.reason) throw error;
    if (deadline.signal.aborted) {
      throw new ApiError({
        status: NETWORK_FAILURE_STATUS,
        code: 'REQUEST_TIMEOUT',
        message:
          method === 'GET'
            ? t('请求超时，请稍后重试')
            : t('请求超时，操作结果尚未确认，请刷新后核对'),
      });
    }
    if (error instanceof ApiError) throw error;
    if (requestWasAborted(error)) throw error;
    throw new ApiError({
      status: NETWORK_FAILURE_STATUS,
      code: NETWORK_ERROR_CODE,
      message: NETWORK_ERROR_MESSAGE,
    });
  } finally {
    clearTimeout(timer);
  }
}

/** The message to show a user for any thrown value. */
export const describeError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.details.length === 0) return error.message;
    const fields = error.details
      .map(
        /** 无字段路径的详情作为整体错误展示。 */ (detail) =>
          detail.path === ''
            ? detail.message
            : `${detail.path}: ${detail.message}`,
      )
      .join(locale === 'en-US' ? '; ' : '；');
    return t('{{message}}（{{fields}}）', { message: error.message, fields });
  }
  if (error instanceof Error) return error.message;
  return t('发生未知错误');
};

/**
 * The API's `details` as a map of field name to messages, for forms that show an error
 * under the input it belongs to (`VALIDATION_FAILED` answers in this shape).
 */
export const fieldErrorsOf = (error: unknown): Record<string, string[]> => {
  if (!(error instanceof ApiError)) return {};
  const fields: Record<string, string[]> = {};
  for (const detail of error.details) {
    if (detail.path === '') continue;
    const messages = fields[detail.path] ?? [];
    messages.push(detail.message);
    fields[detail.path] = messages;
  }
  return fields;
};

/**
 * Where a photo is served from.
 *
 * The API stores a disk and a key, never a URL (`photos` table); the public route is
 * `GET /storage/:file` on the photos disk, and files are named flat, so the key is the
 * whole path.
 */
export const photoUrl = (photo: PhotoLocation): string =>
  buildUrl(`/storage/${encodeURIComponent(photo.key)}`);

/**
 * 会话里的头像是接口源站的绝对地址（`http://localhost:3000/storage/...`）。
 *
 * 页面和接口在浏览器里不是同一个源：开发时分别是 5178 和 3000，部署时
 * `BETTER_AUTH_URL` 也可能不是页面所在的源。`<img>` 跨源不会带上会话 cookie，
 * 存储接口要求登录，图片加载失败后头像就停在文字。本站的 `/storage` 由代理转到
 * 接口，cookie 才能带上。第三方头像（OAuth）保持原地址。
 */
export const sessionImageUrl = (
  image: string | null | undefined,
): string | undefined => {
  if (!image) return undefined;
  const storagePath = storagePathOf(image);
  if (storagePath === undefined) return image;
  return buildUrl(storagePath);
};

const storagePathOf = (image: string): string | undefined => {
  if (image.startsWith('/storage/')) return image;
  if (!image.startsWith('http://') && !image.startsWith('https://')) {
    return undefined;
  }
  try {
    const parsed = new URL(image);
    if (!parsed.pathname.startsWith('/storage/')) return undefined;
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return undefined;
  }
};
